import { useTranslation } from 'react-i18next';
import type { ColumnVisibilityState } from '@tanstack/react-table';

import { Card } from '@/components/atoms/Card';
import Tooltip from '@/components/atoms/Tooltip';
import PageLayout from '@/components/Layout/PageLayout';
import { ModalAddClient } from '@/components/molecules/ModalAddClient';
import { SegmentedControl } from '@/components/molecules/SegmentedControl';
import { ColumnMenu } from '@/components/organisms/DataTable';
import type { ClientSummary } from '@/domain/client';
import UserSearchField from '@/routes/shared/UserManagementPage/Components/UserSearchField';

import { COLUMN_METADATA } from './clientListColumns';
import type { ClientStatusFilter } from './clientStatus';
import ClientTable from './components/ClientTable';

import styles from './Clients.module.scss';

interface ClientsViewProps {
    /** The clients the status filter keeps. */
    clients: ClientSummary[];
    activeCount: number;
    attentionCount: number;
    status: ClientStatusFilter;
    onStatusChange: (status: ClientStatusFilter) => void;
    filter: string;
    onFilterChange: (filter: string) => void;
    columnVisibility: ColumnVisibilityState;
    onColumnVisibilityChange: (visibility: ColumnVisibilityState) => void;
    onColumnToggle: (columnId: string, visible: boolean) => void;
    pageSize: number;
    onPageSizeChange: (pageSize: number) => void;
    onSubmitted: (client: { name: string }) => Promise<void>;
}

const STATUSES: ClientStatusFilter[] = ['active', 'archived', 'all'];

export default function ClientsView({
    clients,
    activeCount,
    attentionCount,
    status,
    onStatusChange,
    filter,
    onFilterChange,
    columnVisibility,
    onColumnVisibilityChange,
    onColumnToggle,
    pageSize,
    onPageSizeChange,
    onSubmitted,
}: ClientsViewProps) {
    const { t } = useTranslation();

    return (
        <PageLayout
            title={t('common.labels.clients')}
            actions={
                <div className={styles.summary}>
                    <span>
                        {t('internal.clients.activeCount')}
                        <strong>{activeCount}</strong>
                    </span>
                    <Tooltip
                        content={t('internal.clients.attentionTooltip')}
                        placement="bottom"
                        layout="inline">
                        {t('internal.clients.attentionCount')}
                        <strong className={styles.attention}>{attentionCount}</strong>
                    </Tooltip>
                </div>
            }>
            <Card as="section" padding="xl">
                <div className={styles.toolbar}>
                    <ModalAddClient onSubmitted={onSubmitted} />
                    <SegmentedControl<ClientStatusFilter>
                        aria-label={t('internal.clients.statusFilter')}
                        size="small"
                        value={status}
                        onChange={onStatusChange}
                        options={STATUSES.map((value) => ({
                            value,
                            label: t(`internal.clients.status.${value}`),
                        }))}
                    />
                    <div className={styles.toolbarEnd}>
                        <UserSearchField filter={filter} setFilter={onFilterChange} />
                        <ColumnMenu
                            columns={COLUMN_METADATA.map((column) => ({
                                id: column.id,
                                label: t(column.header),
                                visible: columnVisibility[column.id] !== false,
                            }))}
                            onToggle={onColumnToggle}
                        />
                    </div>
                </div>
                <ClientTable
                    clients={clients}
                    filter={filter}
                    emptyState={t(`internal.clients.empty.${status}`)}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                />
            </Card>
        </PageLayout>
    );
}
